import { useCallback, useEffect, useState } from 'react';
import { endSessionAndRedirect, LOGIN_REASONS } from '../utils/sessionAuth';

export const SERVICE_INTERRUPTION_RETRY_KEY = 'serviceInterruptionRetryAttempt';

export interface ServiceInterruptionStage {
  retryDelaySeconds: number;
  title: string;
  message: string;
  showProgressBar: boolean;
}

export function getServiceInterruptionStage(attempt: number): ServiceInterruptionStage {
  if (attempt <= 0) {
    return {
      retryDelaySeconds: 15,
      title: 'Downloading latest updates to the software portal',
      message: 'Applying updates now. This is a normal part of keeping your portal current.',
      showProgressBar: true,
    };
  }

  if (attempt === 1) {
    return {
      retryDelaySeconds: 10,
      title: 'Reconnecting to server',
      message: 'Updates are complete. Re-establishing your connection now.',
      showProgressBar: false,
    };
  }

  if (attempt === 2) {
    return {
      retryDelaySeconds: 15,
      title: 'Still working on reconnecting',
      message: 'This is taking a little longer than expected.',
      showProgressBar: false,
    };
  }

  return {
    retryDelaySeconds: 30,
    title: 'Unable to reconnect right now',
    message: 'Something went wrong. Please sign in again or contact Support if this message persists.',
    showProgressBar: false,
  };
}

function readAttemptCount(): number {
  const stored = Number.parseInt(sessionStorage.getItem(SERVICE_INTERRUPTION_RETRY_KEY) || '0', 10);
  return Number.isNaN(stored) ? 0 : Math.max(stored, 0);
}

interface UseServiceInterruptionRetryOptions {
  /** When true, show outage UI and run auto-retry countdown */
  isActive: boolean;
  /** Bump when a retry is triggered so the countdown restarts with the next stage */
  retryGeneration: number;
  /** Called when the countdown elapses or the user chooses Try again */
  onRetry: () => void;
}

export function useServiceInterruptionRetry({
  isActive,
  retryGeneration,
  onRetry,
}: UseServiceInterruptionRetryOptions) {
  const [attempt, setAttempt] = useState(0);
  const [secondsRemaining, setSecondsRemaining] = useState(
    getServiceInterruptionStage(0).retryDelaySeconds
  );

  const stage = getServiceInterruptionStage(attempt);

  useEffect(() => {
    if (!isActive) {
      sessionStorage.removeItem(SERVICE_INTERRUPTION_RETRY_KEY);
      setAttempt(0);
      setSecondsRemaining(getServiceInterruptionStage(0).retryDelaySeconds);
      return;
    }

    const priorAttempts = readAttemptCount();
    const interruptionStage = getServiceInterruptionStage(priorAttempts);
    setAttempt(priorAttempts);
    setSecondsRemaining(interruptionStage.retryDelaySeconds);

    const tickInterval = window.setInterval(() => {
      setSecondsRemaining((prev) => Math.max(prev - 1, 0));
    }, 1000);

    let reloadTimer: number | undefined;
    if (priorAttempts < 3) {
      reloadTimer = window.setTimeout(() => {
        sessionStorage.setItem(SERVICE_INTERRUPTION_RETRY_KEY, String(priorAttempts + 1));
        onRetry();
      }, interruptionStage.retryDelaySeconds * 1000);
    } else {
      reloadTimer = window.setTimeout(() => {
        sessionStorage.removeItem(SERVICE_INTERRUPTION_RETRY_KEY);
        endSessionAndRedirect(LOGIN_REASONS.server_unavailable);
      }, interruptionStage.retryDelaySeconds * 1000);
    }

    return () => {
      window.clearInterval(tickInterval);
      if (reloadTimer !== undefined) {
        window.clearTimeout(reloadTimer);
      }
    };
  }, [isActive, retryGeneration, onRetry]);

  const retryNow = useCallback(() => {
    onRetry();
  }, [onRetry]);

  const returnToLogin = useCallback(() => {
    sessionStorage.removeItem(SERVICE_INTERRUPTION_RETRY_KEY);
    endSessionAndRedirect(LOGIN_REASONS.server_unavailable);
  }, []);

  return {
    attempt,
    secondsRemaining,
    stage,
    retryNow,
    returnToLogin,
    showCountdown: true,
  };
}
